import { useState, type ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Avatar } from '../../Common/Avatar'
import { Pressable } from '../../Common/Pressable'
import type { OverviewSummaryFeedItem } from '../../../services/overviewService'
import type { ViewerTier } from '../../../utils/overviewInsights'

export interface ActivitySectionProps {
  tier: ViewerTier
  feed: OverviewSummaryFeedItem[] | null
  onViewAllActivity?: () => void
  toolsNode?: ReactNode
}

export const ActivitySection = ({
  tier,
  feed,
  onViewAllActivity,
  toolsNode,
}: ActivitySectionProps) => {
  const { t } = useNls()
  const [nowMs] = useState(() => Date.now())

  if (tier === 'helper') {
    return null
  }

  const items = feed ?? []

  const formatRelativeTime = (iso?: string | null) => {
    if (!iso) return ''
    const ms = Date.parse(iso)
    if (!Number.isFinite(ms)) return ''
    const diffMin = Math.max(0, Math.floor((nowMs - ms) / 60000))
    if (diffMin < 60) return `${diffMin}m ago`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return `${diffH}h ago`
    return `${Math.floor(diffH / 24)}d ago`
  }

  return (
    <div className="overviewCard" data-testid="overview-activity-section">
      {toolsNode}
      <div className="overviewCard__header">
        <h3 className="overviewCard__title">{t('NLSOverviewActivityTitle')}</h3>
        {onViewAllActivity && (
          <Pressable
            type="button"
            className="overviewCard__link"
            onClick={onViewAllActivity}
          >
            {t('NLSOverviewActivityAllLink')}
          </Pressable>
        )}
      </div>

      <div className="overviewActivityList">
        {items.slice(0, 8).map((item, idx) => (
          <div key={`${item.actorId}-${idx}`} className="overviewActivityRow">
            <Avatar name={item.actorName} size="xs" />
            <div className="overviewActivityText">
              <strong>{item.actorName.split(' ')[0]}</strong> {item.summary}
            </div>
            {item.createdAt && (
              <span className="overviewActivityTime">
                {formatRelativeTime(item.createdAt)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
